import { AuthService } from '@services';
import { Component, inject, signal } from '@angular/core';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormControlAutocompleteComponent } from '@components/form-control/autocomplete/autocomplete.component';
import { environment } from '@environment';
import { ItemListResponse, Label, searchResource } from '@models';
import { form, FormField, submit } from '@angular/forms/signals';
import { MatDatepickerModule } from '@angular/material/datepicker';

@Component({
  selector: 'app-dataset-filter-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  imports: [
    FormField,

    FormControlAutocompleteComponent,
    LeafletGroupComponent,

    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
  ],
})
export class DatasetFilterComponent {
  // service
  authService = inject(AuthService);

  // dialog
  dialogRef = inject<MatDialogRef<DatasetFilterComponent>>(MatDialogRef);

  labelSearch = signal('');
  labelResource = searchResource<Label[]>(
    () => `${environment.kinkoHost}/label/?type=project&q=${this.labelSearch()}`,
    300,
    {
      parse: (resp) => (resp as ItemListResponse<Label>).items,
    },
  );

  data = inject<{
    search?: string;
    role?: string;
    status?: string;
    label?: Label;
    geojson?: GeoJSON.FeatureCollection;
    order?: string;
    dateField?: 'created' | 'modified' | 'published' | 'timeframe';
    dateFrom?: Date;
    dateUntil?: Date;
  }>(MAT_DIALOG_DATA);

  filterForm = form(signal<{
    search: string;
    label: Label | null;
    role: string;
    status: string;
    geojson: GeoJSON.FeatureCollection | null;
    sort: string | null;
    order: string | null;
    dateField: 'created' | 'modified' | 'published' | 'timeframe';
    dateFrom: Date | null;
    dateUntil: Date | null;
  }>({
    search: this.data.search || '',
    label: this.data.label || null,
    role: this.data.role || '',
    status: this.data.status || '',
    geojson: this.data.geojson || null,
    sort: null,
    order: null,
    dateField: this.data.dateField || 'timeframe',
    dateFrom: typeof this.data.dateFrom === "string" ? new Date(this.data.dateFrom) : this.data.dateFrom || null,
    dateUntil: typeof this.data.dateUntil === "string" ? new Date(this.data.dateUntil) : this.data.dateUntil || null,
  }));

  constructor() {
    if (this.data.order) {
      const parts = this.data.order.split(':');
      if (parts.length == 2) {
        this.filterForm.sort().value.set(parts[0]);
        this.filterForm.order().value.set(parts[1]);
      }
    }
  }

  onGeoSearch(geometry: GeoJSON.FeatureCollection | undefined) {
    this.filterForm.geojson().value.set(geometry || null);
  }

  labelDisplayWith(l: Label) {
    return `${l.title}`;
  }

  submit(event: Event) {
    event.preventDefault()

    submit(this.filterForm, async () => {
      const sort = this.filterForm.sort().value();
      const order = this.filterForm.order().value();

      this.dialogRef.close({
        search: this.filterForm.search().value() || undefined,
        role: this.filterForm.role().value() || undefined,
        status: this.filterForm.status().value() || undefined,
        label: this.filterForm.label().value() || undefined,
        geojson: this.filterForm.geojson().value() || undefined,
        order: sort || order
          ? `${sort || 'created'}:${order || 'desc'}`
          : undefined,
        dateField: this.filterForm.dateField().value(),
        dateFrom: this.filterForm.dateFrom().value(),
        dateUntil: this.filterForm.dateUntil().value(),
      });
    });
  }
}
